
import { defineComponent } from 'vue';
import { mapGetters } from 'vuex';
import Modal from '@/components/ui/Modal.vue';
import ContentLoader from '@/components/ui/ContentLoader.vue';
import { ProposalAction } from '@/store/proposal/proposal.action';
import { ProposalMutation } from '@/store/proposal/proposal.mutation';
import { AppMutation } from '@/store/app/app.mutation';
import { NotificationType } from '@/store/app/notification/notification-type';
import { ProductCostPrice } from '@/model/product-cost-price';
import { SuppliersSelectOption } from '@/model/suppliers-choices';
import proposalsService from '@/services/proposals.service';

interface NewSurchargeForm {
  name: string;
  supplierId: string;
  serviceName: string;
  countryId: string;
  priceAmount: number | null;
  grossMargin: number;
}

interface AddSurchargeModalState {
  activeTab: 'existing' | 'create';
  selectedIds: string[];
  isLoadingSurcharges: boolean;
  isSubmitting: boolean;
  newSurcharge: NewSurchargeForm;
}


export default defineComponent({
  name: 'AddSurchargeModal',
  components: { Modal, ContentLoader },
  props: {
    modelValue: {
      type: Boolean,
      required: true
    },
    proposalId: {
      type: String,
      required: true
    }
  },
  emits: ['update:modelValue'],
  data(): AddSurchargeModalState {
    return {
      activeTab: 'existing',
      selectedIds: [],
      isLoadingSurcharges: false,
      isSubmitting: false,
      newSurcharge: {
        name: '',
        supplierId: '',
        serviceName: '',
        countryId: '',
        priceAmount: null,
        grossMargin: 0
      }
    };
  },
  watch: {
    modelValue(val: boolean): void {
      if (val) {
        this.reset();
        this.loadAvailableSurcharges();
        if (!this.allCountries.length) {
          this.$store.dispatch(ProposalAction.LOAD_PROPOSAL_ALL_COUNTRIES);
        }
      }
    }
  },
  computed: {
    ...mapGetters(['availableSurcharges', 'proposalSuppliers', 'allCountries']),
    show: {
      get(): boolean {
        return this.modelValue;
      },
      set(val: boolean): void {
        this.$emit('update:modelValue', val);
      }
    },
    supplierOptions(): SuppliersSelectOption[] {
      return this.proposalSuppliers;
    },
    effectiveSupplierId(): string {
      if (this.supplierOptions.length === 1) {
        return this.supplierOptions[0].id;
      }
      return this.newSurcharge.supplierId;
    },
    canConfirm(): boolean {
      if (this.isSubmitting) return false;
      if (this.activeTab === 'existing') {
        return this.selectedIds.length > 0;
      }
      const { name, priceAmount } = this.newSurcharge;
      const supplierOk = this.supplierOptions.length === 1 || !!this.newSurcharge.supplierId;
      return !!name.trim() && priceAmount !== null && priceAmount >= 0 && supplierOk;
    }
  },
  methods: {
    reset(): void {
      this.activeTab = 'existing';
      this.selectedIds = [];
      this.newSurcharge = {
        name: '',
        supplierId: '',
        serviceName: '',
        countryId: '',
        priceAmount: null,
        grossMargin: 0
      };
    },
    toggleSelection(id: string): void {
      const idx = this.selectedIds.indexOf(id);
      if (idx === -1) {
        this.selectedIds.push(id);
      } else {
        this.selectedIds.splice(idx, 1);
      }
    },
    async loadAvailableSurcharges(): Promise<void> {
      this.isLoadingSurcharges = true;
      try {
        await this.$store.dispatch(ProposalAction.LOAD_AVAILABLE_SURCHARGES, this.proposalId);
      } finally {
        this.isLoadingSurcharges = false;
      }
    },
    async handleConfirm(): Promise<void> {
      if (this.activeTab === 'existing') {
        this.addSelectedSurcharges();
      } else {
        await this.createNewSurcharge();
      }
    },
    addSelectedSurcharges(): void {
      const toAdd = (this.availableSurcharges as ProductCostPrice[])
          .filter((s: ProductCostPrice) => this.selectedIds.includes(s.id));

      toAdd.forEach((surcharge: ProductCostPrice) => {
        this.$store.commit(ProposalMutation.ADD_SURCHARGE_ROW, surcharge);
      });
    },
    async createNewSurcharge(): Promise<void> {
      this.isSubmitting = true;
      try {
        const priceCents = Math.round((this.newSurcharge.priceAmount ?? 0) * 100);
        const response = await proposalsService.createSurcharge(this.proposalId, {
          name: this.newSurcharge.name.trim(),
          supplierId: this.effectiveSupplierId,
          serviceName: this.newSurcharge.serviceName.trim() || undefined,
          countryId: this.newSurcharge.countryId || undefined,
          priceCents,
          grossMargin: this.newSurcharge.grossMargin
        });

        if (response.data.alreadyExists) {
          this.$store.commit(AppMutation.ADD_NOTIFICATION, {
            type: NotificationType.INFO,
            title: 'general.notifications.info',
            content: 'This surcharge is already in the proposal.'
          });
        } else {
          this.$store.commit(ProposalMutation.ADD_SALE_PRICE_SURCHARGE_ROW, response.data);
          this.$store.commit(AppMutation.ADD_NOTIFICATION, {
            type: NotificationType.SUCCESS,
            title: 'general.notifications.success',
            content: 'Surcharge created and added to the proposal.'
          });
        }
      } catch {
        this.$store.commit(AppMutation.ADD_NOTIFICATION, {
          type: NotificationType.ERROR,
          title: 'general.notifications.error',
          content: 'Failed to create surcharge. Please try again.'
        });
      } finally {
        this.isSubmitting = false;
      }
    }
  }
});
